import { reportError } from "../ops/report";
import { useStore } from "../store";
import { type ClientBody, TableSocket } from "./client";

let current: TableSocket | null = null;
/** Starts the Activity over with a fresh session; the app hands it in. */
let restart: (() => void) | null = null;
/** A fresh session was already tried since the socket last opened. */
let retried = false;

export function setRestart(next: (() => void) | null): void {
  restart = next;
}

/**
 * Three failures in a row (review 0027 C2, C3). At a table the session has most likely run out:
 * sign in again, quietly, once. With no table on screen, or when that did not help either, say so
 * instead of turning the spinner for ever.
 */
function unreachable(): void {
  const { snapshot, set } = useStore.getState();
  if (snapshot !== null && !retried && restart !== null) {
    retried = true;
    restart();
    return;
  }
  const eventId = reportError("error", new Error("table socket unreachable"), { stage: "socket" });
  setSocket(null);
  set({ phase: "failed", error: "server", diagnostics: null, eventId });
}

/** The table we asked for is full of spectators (D7): back to this channel's own table, and say why. */
function refused(): void {
  const { instanceId, set } = useStore.getState();
  if (instanceId !== null) openTable(instanceId);
  set({ lastError: "spectators-full" });
}

/** The server speaks a newer protocol (C1): the socket has stopped; tell the player to reload. */
function stale(): void {
  reportError("error", new Error("stale protocol"), { stage: "socket" });
  useStore.getState().set({ stale: true });
}

export function setSocket(socket: TableSocket | null): void {
  current?.close();
  current = socket;
}

/** Screens send through here; a message before the socket exists is dropped on purpose. */
export function send(message: ClientBody): void {
  current?.send(message);
}

/**
 * Leave the table for good (mid-game a bot takes the seat). If you were the last
 * person the server closes the table; that is a leave, not a lost connection.
 */
export function leaveTable(): void {
  useStore.getState().set({ leaving: true });
  send({ type: "leave" });
}

/** Moves the socket to another table (open tables, a link, or back to this channel's own). */
export function openTable(tableId: string): void {
  const { session, guildId, apply, set } = useStore.getState();
  if (session === null) return;
  set({ tableId, snapshot: null, lastError: null });
  setSocket(
    new TableSocket(
      tableId,
      session,
      {
        onMessage: apply,
        onConnection: (connection) => {
          if (connection === "open") retried = false;
          set({ connection, connectionAt: Date.now() });
        },
        onStale: stale,
        onUnreachable: unreachable,
        onRefused: refused,
      },
      guildId,
    ),
  );
}
